import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { getLegalBases, getDataCategories } from 'services/mapping-service';

const CATEGORY_COLORS = ['var(--vo-warning)', 'var(--vo-success)', 'var(--vo-purple)', 'var(--vo-sev-high)', 'var(--vo-steel)', 'var(--vo-emerald-600)', 'color-mix(in srgb, var(--vo-sev-high) 60%, black)', 'var(--vo-slate)'];

const RowBlock = styled.div`
  margin-bottom: 14px;
  &:last-child { margin-bottom: 0; }
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
`;

const RowName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--vo-ink-2);
`;

const RowMeta = styled.span`
  font-size: 11px;
  color: var(--vo-slate);
`;

const StackedBar = styled.div`
  display: flex;
  height: 16px;
  border-radius: 4px;
  overflow: hidden;
  background: var(--vo-mist);
`;

const Seg = styled.div`
  height: 100%;
  background: ${p => p.$color};
  flex-shrink: 0;
  width: ${p => p.$pct}%;
`;

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--vo-mist);
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--vo-slate);
`;

const Swatch = styled.div`
  width: 10px;
  height: 10px;
  background: ${p => p.$color};
  border-radius: 2px;
  flex-shrink: 0;
`;

const Empty = styled.div`
  font-size: 13px;
  color: var(--vo-steel);
`;

export const GraphicLegalBaseVolumeData = ({ indicatorVolumeGraphicByLegalBase }) => {
  const { t, i18n } = useTranslation('mapping');
  const selectedTenant = useSelector(useAuthTenant);

  const { data: legalBasesList } = useQuery(
    ['getLegalBases', selectedTenant?.slug, i18n.language],
    async () => {
      if (!selectedTenant?.slug) return [];
      const result = await getLegalBases(selectedTenant.slug);
      return result?.data?.data ?? [];
    },
  );

  const { data: dataCategoriesList } = useQuery(
    ['getDataCategories', selectedTenant?.slug, i18n.language],
    async () => {
      if (!selectedTenant?.slug) return [];
      const result = await getDataCategories(selectedTenant.slug);
      return result?.data?.data ?? [];
    },
  );

  const categoryByKey = new Map((dataCategoriesList ?? []).map((c) => [c.key, c.name]));

  const categoryKeys = Array.from(
    new Set(
      (indicatorVolumeGraphicByLegalBase ?? []).flatMap((row) =>
        (row.categoryCounts ?? []).map((cc) => cc.categoryKey),
      ),
    ),
  );

  const legalBases = (legalBasesList ?? []).map((lb) => {
    const match = (indicatorVolumeGraphicByLegalBase ?? []).find(
      (item) => item.name === lb.key || item.name === lb.name || item.key === lb.key,
    );
    const processQuantity = match?.processQuantity ?? 0;
    const areaQuantity = match?.areaQuantity ?? 0;
    const segments = [
      { key: 'processQuantity', value: processQuantity, color: 'var(--vo-celeste)' },
      ...categoryKeys.map((categoryKey, idx) => {
        const entry = (match?.categoryCounts ?? []).find((cc) => cc.categoryKey === categoryKey);
        return {
          key: `category_${categoryKey}`,
          value: entry?.quantity ?? 0,
          color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length],
        };
      }),
      { key: 'otherQuantity', value: match?.otherQuantity ?? 0, color: 'var(--vo-steel)' },
      { key: 'areaQuantity', value: areaQuantity, color: 'var(--vo-capri)' },
    ];
    const total = segments.reduce((sum, s) => sum + s.value, 0);
    return { name: lb.name, segments, total, processQuantity, areaQuantity };
  });

  const visibleBases = legalBases.filter(r => r.total > 0);
  const maxTotal = Math.max(1, ...visibleBases.map(r => r.total));

  const legendItems = [
    { key: 'processQuantity', color: 'var(--vo-celeste)', label: t('ropa.area.processes') },
    ...categoryKeys.map((k, i) => ({
      key: `category_${k}`,
      color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
      label: categoryByKey.get(k) ?? k,
    })),
    { key: 'otherQuantity', color: 'var(--vo-steel)', label: t('ropa.executiveSummary.otherDataPlural') },
    { key: 'areaQuantity', color: 'var(--vo-capri)', label: t('ropa.executiveSummary.areasPlural') },
  ];

  const labelByKey = Object.fromEntries(legendItems.map((li) => [li.key, li.label]));

  if (visibleBases.length === 0) {
    return <Empty>{t('ropa.executiveSummary.noData')}</Empty>;
  }

  return (
    <div>
      {visibleBases.map((row, i) => {
        const middleMeta = row.segments
          .filter((seg) => seg.key !== 'processQuantity' && seg.key !== 'areaQuantity')
          .map((seg) => `${seg.value} ${labelByKey[seg.key] ?? seg.key}`)
          .join(' · ');
        const rowMetaText = [
          middleMeta,
          t('ropa.executiveSummary.procAndAreasMeta', { proc: row.processQuantity, areas: row.areaQuantity }),
        ].filter(Boolean).join(' · ');
        return (
          <RowBlock key={i}>
            <RowHeader>
              <RowName>{row.name}</RowName>
              <RowMeta>{rowMetaText}</RowMeta>
            </RowHeader>
            <StackedBar>
              {row.segments.map(seg => (
                <Seg key={seg.key} $color={seg.color} $pct={(seg.value / maxTotal) * 100} />
              ))}
            </StackedBar>
          </RowBlock>
        );
      })}
      <LegendRow>
        {legendItems.map((item, i) => (
          <LegendItem key={i}>
            <Swatch $color={item.color} />
            {item.label}
          </LegendItem>
        ))}
      </LegendRow>
    </div>
  );
};
